import { useEffect, useState } from "react";
import { Database, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { etichetaSurseDate } from "@/utils/surseDateFormatting";
import type { FetchCompanyDataResultStatus } from "@/hooks/useOnboardingForm";

type AutoFillBannerProps = {
  cui: string;
  variant: "company" | "financial";
  isFetching: boolean;
  fetchResult: FetchCompanyDataResultStatus;
  onFetch: () => void;
};

function AutoFillBanner({
  cui,
  variant,
  isFetching,
  fetchResult,
  onFetch,
}: AutoFillBannerProps) {
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (fetchResult.status === "success") {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
    setShowSuccess(false);
  }, [fetchResult]);

  const hasCui = Boolean(cui && cui.trim().length > 0);

  const etichetaSurse = etichetaSurseDate(fetchResult.surse);

  if (showSuccess && fetchResult.filledCount) {
    return (
      <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg bg-ds-emerald-50 border border-ds-emerald-200 transition-opacity duration-500 min-h-[50px]">
        <CheckCircle2 className="h-4 w-4 text-ds-emerald-600 shrink-0" />
        <span className="text-xs text-ds-emerald-700 font-medium">
          {fetchResult.filledCount} câmpuri completate
          {etichetaSurse ? ` (${etichetaSurse})` : " automat"}
        </span>
      </div>
    );
  }

  if (fetchResult.status === "error") {
    return (
      <div className="flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg bg-ds-red-50 border border-ds-red-200 min-h-[50px]">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-ds-red-500 shrink-0" />
          <span className="text-xs text-ds-red-600">
            {fetchResult.error || "Eroare la preluarea datelor"}
          </span>
        </div>
        <button
          type="button"
          onClick={onFetch}
          className="text-xs text-ds-red-600 hover:text-ds-red-800 underline underline-offset-2 shrink-0"
        >
          Reîncearcă
        </button>
      </div>
    );
  }

  if (!hasCui) {
    if (variant === "financial") {
      return (
        <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg bg-ds-surface-50 border border-ds-border-200 min-h-[50px]">
          <Database className="h-4 w-4 text-ds-text-400 shrink-0" />
          <span className="text-xs text-ds-text-500">
            Completează{" "}
            <button
              type="button"
              className="text-ds-blue-600 hover:text-ds-blue-800 underline underline-offset-2"
              onClick={() => {
                const cuiField = document.getElementById("cui");
                cuiField?.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                setTimeout(() => cuiField?.focus(), 400);
              }}
            >
              CUI-ul în secțiunea Identificare Companie
            </button>{" "}
            pentru a prelua datele financiare automat
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 py-2.5 px-3 rounded-lg bg-ds-surface-50 border border-ds-border-200 min-h-[50px]">
        <Database className="h-4 w-4 text-ds-text-400 shrink-0" />
        <span className="text-xs text-ds-text-500">
          Introdu CUI-ul companiei pentru a prelua datele automat din registre
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg min-h-[50px]",
        "bg-ds-blue-50/70 border border-ds-blue-200"
      )}
    >
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-ds-blue-500 shrink-0" />
        <span className="text-xs text-ds-blue-700">
          Preia automat datele companiei din registre
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        variant="default"
        onClick={onFetch}
        disabled={isFetching}
        className="shrink-0 h-7 text-xs"
      >
        {isFetching ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            Se preiau datele...
          </>
        ) : (
          "Preia date ONRC"
        )}
      </Button>
    </div>
  );
}

export default AutoFillBanner;
